import { useEffect, useRef, useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronUp, ListMusic, MonitorSpeaker, Pause, Play, SkipBack, SkipForward, X } from 'lucide-react'
import { useNow } from '@/hooks/useNow'
import { usePlayer } from '@/state/player'
import { api } from '@/runtime'
import type { PlayRequest, SpotifyDevice } from '@/db/types'
import { Button, IconButton, Sheet, cx } from './ui'

const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * A progress line you can scrub: press anywhere on it (or drag) to seek. The
 * visible line is thin; the hit area is not. While the pointer is down the
 * line follows the finger and the seek is sent on release, so a drag is one
 * request rather than a stream of them.
 */
export const SeekBar = ({
  progress,
  onSeek,
  className,
}: {
  /** 0..1 as it is now */
  progress: number
  /** fraction of the track to go to */
  onSeek: (fraction: number) => void
  className?: string
}) => {
  const [drag, setDrag] = useState<number | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const at = (clientX: number) => {
    const r = ref.current?.getBoundingClientRect()
    if (!r || r.width === 0) return 0
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width))
  }
  const shown = drag ?? progress
  return (
    <div
      ref={ref}
      role="slider"
      aria-label="Position"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(shown * 100)}
      tabIndex={0}
      className={cx('group relative cursor-pointer touch-none select-none', 'py-1.5', className)}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId)
        setDrag(at(e.clientX))
      }}
      onPointerMove={(e) => {
        if (drag !== null) setDrag(at(e.clientX))
      }}
      onPointerUp={(e) => {
        const f = at(e.clientX)
        setDrag(null)
        onSeek(f)
      }}
      onPointerCancel={() => setDrag(null)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') onSeek(Math.max(0, progress - 0.05))
        if (e.key === 'ArrowRight') onSeek(Math.min(1, progress + 0.05))
      }}
      data-testid="seek-bar"
    >
      <div className={cx('rounded-full overflow-hidden', 'h-0.5 bg-line')}>
        <div className="h-full bg-accent rounded-full" style={{ width: `${shown * 100}%` }} />
      </div>
      <span
        className={cx(
          'absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-accent shadow transition-opacity',
          'w-3 h-3',
          drag !== null ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
        style={{ left: `${shown * 100}%` }}
        aria-hidden
      />
    </div>
  )
}

/**
 * The bar at the foot of every page while Spotify is playing: art, title,
 * progress that runs on the local clock between polls, transport, and the
 * device it is on. Hidden when nothing is playing, so a quiet app stays quiet.
 */
export const PlayerBar = ({ className }: { className?: string }) => {
  const state = usePlayer((s) => s.state)
  const control = usePlayer((s) => s.control)
  const watch = usePlayer((s) => s.watch)
  const error = usePlayer((s) => s.error)
  const now = useNow(250)
  const [devices, setDevices] = useState<SpotifyDevice[] | null>(null)
  const [devicesOpen, setDevicesOpen] = useState(false)
  // Music's own Now card has the Play on chips: opened there, the bar adds only the seek row
  const onMusic = useRouterState({ select: (s) => s.location.pathname === '/music' })
  useEffect(() => watch(), [watch])
  // while the bar shows, its height (and the gap under it) is --player-h, so
  // composers and toolbars above the tab bar sit above the player too
  const [bar, setBar] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    const root = document.documentElement
    if (!bar) return
    const ro = new ResizeObserver(() => root.style.setProperty('--player-h', `${bar.offsetHeight + 8}px`))
    ro.observe(bar)
    return () => {
      ro.disconnect()
      root.style.setProperty('--player-h', '0px')
    }
  }, [bar])
  // the device list is fetched when the bar shows and refreshed when the select opens,
  // so the select always has something to offer and rarely has to wait
  const connected = !!state?.connected && !!state.title
  useEffect(() => {
    if (!connected) return
    let stale = false
    api
      .spotifyDevices()
      .then((r) => {
        if (!stale) setDevices(r.devices)
      })
      .catch(() => {})
    return () => {
      stale = true
    }
  }, [connected, state?.device])
  if (!state?.connected || !state.title) return null
  const base = state.progress_ms + (state.playing ? Math.max(0, now - Date.parse(state.fetched_at)) : 0)
  const progress = state.duration_ms ? Math.min(1, base / state.duration_ms) : 0
  const seek = (f: number) => {
    if (!state.duration_ms) return
    void control('seek', { position_ms: Math.round(f * state.duration_ms) }).catch(() => {})
  }
  const switchTo = (id: string) => {
    if (!id) return
    void control('play', { device_id: id }).catch(() => {})
    setDevicesOpen(false)
  }
  const refreshDevices = async () => {
    try {
      setDevices((await api.spotifyDevices()).devices)
    } catch {
      setDevices([])
    }
  }
  const current = devices?.find((d) => d.active)?.id ?? ''
  return (
    <div
      ref={setBar}
      // a floating card on the phone, like the tab bar under it, so nothing shows half
      // through a strip between them; a solid bar along the foot of a desk. Solid
      // either way: frosted glass is the tab bar's alone
      className={cx(
        'bg-panel border-line overflow-hidden max-md:mx-2 max-md:border max-md:rounded-[var(--radius-card)] max-md:shadow-[var(--shadow-pop)] md:border-t',
        className,
      )}
      data-testid="player-bar"
    >
      {/* opened on the phone, the big seek row below takes over from the edge line */}
      <SeekBar progress={progress} onSeek={seek} className={cx('-mb-1.5', devicesOpen && 'max-md:hidden')} />
      <div className="flex items-center gap-3 px-3 py-2">
        <Link to="/music" className="flex items-center gap-3 min-w-0 flex-1">
          {state.image ? (
            <img src={state.image} alt="" className="w-10 h-10 rounded-[6px] object-cover shrink-0" />
          ) : (
            <span className="w-10 h-10 rounded-[6px] bg-raised shrink-0" />
          )}
          <span className="min-w-0">
            <span className="block text-sm truncate">{state.title}</span>
            <span className="flex gap-x-2.5 text-xs text-faint min-w-0">
              <span className="truncate">{state.artist}</span>
              {state.device && <span className="truncate max-md:hidden">{state.device}</span>}
              <span className="shrink-0 tabular-nums max-md:hidden">
                {mmss(base)} of {mmss(state.duration_ms)}
              </span>
            </span>
          </span>
        </Link>
        <div className="flex items-center gap-0.5 shrink-0">
          <IconButton label="Previous" onClick={() => void control('previous').catch(() => {})}>
            <SkipBack size={18} />
          </IconButton>
          <IconButton
            label={state.playing ? 'Pause' : 'Play'}
            onClick={() => void control(state.playing ? 'pause' : 'play').catch(() => {})}
            data-testid="player-toggle"
          >
            {state.playing ? <Pause size={20} /> : <Play size={20} />}
          </IconButton>
          <IconButton label="Next" onClick={() => void control('next').catch(() => {})}>
            <SkipForward size={18} />
          </IconButton>
          {/* the device: a select where there is room for one, a sheet of chips on the phone */}
          <label className="hidden md:inline-flex items-center gap-1.5 ml-1 pl-2 pr-1 h-8 rounded-full bg-raised text-xs text-muted max-w-[180px]">
            <MonitorSpeaker size={14} className="shrink-0" />
            <select
              aria-label="Play on"
              value={current}
              onFocus={() => void refreshDevices()}
              onChange={(e) => switchTo(e.target.value)}
              className="bare-input !text-xs !p-0 !pr-4 !h-auto bg-transparent truncate max-w-[140px]"
              data-testid="player-device-select"
            >
              {(devices ?? []).length === 0 && <option value="">No device open</option>}
              {(devices ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <IconButton
            label={devicesOpen ? 'Close the player' : 'Open the player'}
            aria-expanded={devicesOpen}
            onClick={() => {
              setDevicesOpen((o) => !o)
              void refreshDevices()
            }}
            className="md:hidden"
          >
            <ChevronUp size={16} className={cx('transition-transform', devicesOpen && 'rotate-180')} />
          </IconButton>
        </div>
      </div>
      {devicesOpen && (
        <div className="px-3 pb-2.5 md:hidden" data-testid="player-open">
          {/* opened: a seek row big enough for a thumb, with the times */}
          <SeekBar progress={progress} onSeek={seek} className="py-2.5" />
          <div className="flex justify-between text-[11px] text-faint tabular-nums -mt-1">
            <span>{mmss(base)}</span>
            <span>{mmss(state.duration_ms)}</span>
          </div>
          {!onMusic && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="player-devices">
              {(devices ?? []).length === 0 && (
                <span className="text-xs text-faint">No Spotify device is open.</span>
              )}
              {(devices ?? []).length > 0 && <span className="text-xs text-faint mr-1">Play on</span>}
              {(devices ?? []).map((d) => (
                <Button key={d.id} variant={d.active ? 'chipOn' : 'chip'} onClick={() => switchTo(d.id)}>
                  {d.name}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
      {error && (
        <div className="px-3 pb-2 text-xs text-fg-2" role="status">
          {error}
        </div>
      )}
    </div>
  )
}

/** A play button for a track, album or artist row; the server resolves what to send Spotify. */
export const PlayButton = ({
  play,
  title,
  uri,
  className,
  small,
}: {
  play: PlayRequest
  title?: string
  uri?: string
  className?: string
  small?: boolean
}) => {
  const doPlay = usePlayer((s) => s.play)
  return (
    <button
      type="button"
      aria-label={`Play ${title ?? ''}`.trim()}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void doPlay({ ...play, ...(uri ? { uri } : {}) }, title).catch(() => {})
      }}
      className={cx(
        'inline-flex items-center justify-center rounded-full bg-accent text-on-accent shrink-0 hover:brightness-110 active:scale-95 transition',
        small ? 'w-7 h-7' : 'w-9 h-9',
        className,
      )}
      data-testid="play-button"
    >
      <Play size={small ? 12 : 16} className="ml-0.5" />
    </button>
  )
}

/**
 * Spotify's own embed in a sheet: a 30-second preview for anyone, the full
 * track when the viewer is signed in to Spotify in the browser. The one
 * frame the CSP allows.
 */
export const EmbedDrawer = () => {
  const embed = usePlayer((s) => s.embed)
  const close = usePlayer((s) => s.closeEmbed)
  if (!embed) return null
  const m = /^spotify:(track|album|artist|playlist):([A-Za-z0-9]+)$/.exec(embed.uri)
  if (!m) return null
  const src = `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator&theme=0`
  return (
    <Sheet open onClose={close} title={embed.title ?? 'Preview'}>
      <div
        className="rounded-[var(--radius-sheet-inner)] overflow-hidden bg-black"
        data-testid="embed-drawer"
      >
        <iframe
          title={embed.title ?? 'Spotify'}
          src={src}
          width="100%"
          height={m[1] === 'track' ? 152 : 352}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{ border: 0 }}
        />
      </div>
      <p className="text-xs text-faint mt-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1">
          <ListMusic size={12} /> A preview from Spotify; sign in there for the whole track.
        </span>
        <button type="button" onClick={close} className="inline-flex items-center gap-1 underline">
          <X size={12} /> Close
        </button>
      </p>
    </Sheet>
  )
}
